import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { FileText, ShoppingBag, CreditCard, Heart, MessageSquare, Settings, LogOut, Download, CalendarDays, Sparkles, Star, AlertCircle, Package } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useUser } from '../contexts/UserContext';
import { zodiacThemes, getZodiacSign } from '../components/user-dashboard/zodiacThemes';
import { OverviewTab } from '../components/user-dashboard/OverviewTab';
import { ProfileTab } from '../components/user-dashboard/ProfileTab';
import { CalendarTab } from '../components/user-dashboard/CalendarTab';
import { ReportsTab } from '../components/user-dashboard/ReportsTab';
import { OrdersTab } from '../components/user-dashboard/OrdersTab';
import { CreditsTab } from '../components/user-dashboard/CreditsTab';
import { SubscriptionsTab } from '../components/user-dashboard/SubscriptionsTab';
import { FavoritesTab } from '../components/user-dashboard/FavoritesTab';
import { ChatTab } from '../components/user-dashboard/ChatTab';
import { SettingsTab } from '../components/user-dashboard/SettingsTab';
import { PurchasesTab } from '../components/user-dashboard/PurchasesTab';
interface UserDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function UserDashboardPage({
  onNavigate
}: UserDashboardPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const {
    user,
    logout
  } = useUser();
  // User Astro Data State
  const [birthData, setBirthData] = useState({
    date: '1990-04-12',
    time: '14:30',
    place: 'Москва, Россия'
  });
  const currentSign = getZodiacSign(birthData.date);
  const theme = zodiacThemes[currentSign];
  const menuItems = [{
    id: 'overview',
    label: 'Обзор',
    icon: <FileText className="w-5 h-5" data-id="element-22640" />
  }, {
    id: 'profile',
    label: 'Астро-профиль',
    icon: <Sparkles className="w-5 h-5" data-id="element-22641" />
  }, {
    id: 'calendar',
    label: 'Мой календарь',
    icon: <CalendarDays className="w-5 h-5" data-id="element-22642" />
  }, {
    id: 'reports',
    label: 'Мои отчёты',
    icon: <Download className="w-5 h-5" data-id="element-22643" />
  }, {
    id: 'orders',
    label: 'Заказы',
    icon: <ShoppingBag className="w-5 h-5" data-id="element-22644" />
  }, {
    id: 'purchases',
    label: 'Мои покупки',
    icon: <Package className="w-5 h-5" data-id="element-22645" />
  }, {
    id: 'credits',
    label: 'Кредиты',
    icon: <CreditCard className="w-5 h-5" data-id="element-22646" />
  }, {
    id: 'subscriptions',
    label: 'Подписки',
    icon: <Star className="w-5 h-5" data-id="element-22647" />
  }, {
    id: 'favorites',
    label: 'Избранное',
    icon: <Heart className="w-5 h-5" data-id="element-22648" />
  }, {
    id: 'chat',
    label: 'Сообщения',
    icon: <MessageSquare className="w-5 h-5" data-id="element-22649" />
  }, {
    id: 'settings',
    label: 'Настройки',
    icon: <Settings className="w-5 h-5" data-id="element-22650" />
  }];
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab theme={theme} user={user} setActiveTab={setActiveTab} data-id="element-22651" />;
      case 'profile':
        return <ProfileTab theme={theme} birthData={birthData} setBirthData={setBirthData} setActiveTab={setActiveTab} data-id="element-22652" />;
      case 'calendar':
        return <CalendarTab theme={theme} data-id="element-22653" />;
      case 'reports':
        return <ReportsTab theme={theme} onNavigate={onNavigate} data-id="element-22654" />;
      case 'orders':
        return <OrdersTab theme={theme} onNavigate={onNavigate} setActiveTab={setActiveTab} data-id="element-22655" />;
      case 'purchases':
        return <PurchasesTab onNavigate={onNavigate} data-id="element-22656" />;
      case 'credits':
        return <CreditsTab theme={theme} data-id="element-22657" />;
      case 'subscriptions':
        return <SubscriptionsTab theme={theme} onNavigate={onNavigate} data-id="element-22658" />;
      case 'favorites':
        return <FavoritesTab theme={theme} onNavigate={onNavigate} data-id="element-22659" />;
      case 'chat':
        return <ChatTab theme={theme} data-id="element-22660" />;
      case 'settings':
        return <SettingsTab theme={theme} user={user} data-id="element-22661" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-22662">
            <AlertCircle className="w-12 h-12 mb-4 text-slate-300" data-id="element-22663" />
            <p data-id="element-22664">Раздел в разработке</p>
          </div>;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24 font-sans" data-id="element-22665">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-22666">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-22667">
          {/* Sidebar */}
          <div className="lg:col-span-3" data-id="element-22668">
            {/* Mobile Tabs */}
            <div className="lg:hidden mb-6" data-id="element-22669">
              <div className={`flex items-center gap-3 mb-4 p-4 rounded-2xl bg-gradient-to-r ${theme.lightGradient} border ${theme.border}`} data-id="element-22670">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white font-bold text-lg shadow-sm`} data-id="element-22671">
                  {theme.icon}
                </div>
                <div data-id="element-22672">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-22673">
                    {user?.name || 'Пользователь'}
                  </div>
                  <div className={`text-xs font-medium ${theme.text}`} data-id="element-22674">
                    {theme.name}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-22675">
                {menuItems.map(item => <button key={item.id} onClick={() => setActiveTab(item.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${activeTab === item.id ? `${theme.bg} ${theme.text} ring-1 ${theme.ring}` : 'bg-white border border-slate-200 text-slate-600'}`} data-id="element-22676">
                    {cloneElement(item.icon as React.ReactElement, {
                  className: 'w-3.5 h-3.5'
                })}
                    {item.label}
                  </button>)}
              </div>
            </div>

            {/* Desktop Sidebar */}
            <Card padding="none" className="sticky top-24 overflow-hidden hidden lg:block shadow-sm" data-id="element-22677">
              <div className={`p-6 border-b ${theme.border} bg-gradient-to-b ${theme.lightGradient}`} data-id="element-22678">
                <div className="flex flex-col items-center text-center" data-id="element-22679">
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-3xl text-white shadow-md mb-3 relative`} data-id="element-22680">
                    {theme.icon}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold text-slate-700 shadow-sm uppercase" data-id="element-22681">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="font-bold text-slate-900 text-lg" data-id="element-22682">
                    {user?.name || 'Пользователь'}
                  </div>
                  <div className={`text-xs font-bold uppercase tracking-wider mt-1 ${theme.text}`} data-id="element-22683">
                    {theme.name}
                  </div>
                </div>
              </div>
              <nav className="p-3 space-y-1" data-id="element-22684">
                {menuItems.map(item => <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === item.id ? `${theme.bg} ${theme.text} shadow-sm` : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-22685">
                    <span className={`mr-3 ${activeTab === item.id ? theme.text : 'text-slate-400'}`} data-id="element-22686">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>)}
                <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-22687">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-22688">
                    <LogOut className="w-5 h-5 mr-3 text-rose-400" data-id="element-22689" /> Выйти
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-9" data-id="element-22690">
            <SectionErrorBoundary key={activeTab} sectionName={`dashboard-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-22691">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>
    </div>;
}